'use client';

import React from 'react';
import { twMerge } from 'tailwind-merge';

export type InputVariant = 'outlined' | 'soft' | 'plain';
export type InputSize = 'sm' | 'md' | 'lg';

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  variant?: InputVariant;
  size?: InputSize;
  startDecorator?: React.ReactNode;
  endDecorator?: React.ReactNode;
  error?: boolean;
  fullWidth?: boolean;
  sx?: React.CSSProperties;
  slotProps?: {
    root?: { className?: string };
    input?: { className?: string };
    startDecorator?: { className?: string };
    endDecorator?: { className?: string };
  };
}

const getContainerClasses = (
  variant: InputVariant,
  size: InputSize,
  error: boolean,
  fullWidth: boolean,
  disabled: boolean | undefined,
  focused: boolean
) => {
  const baseClasses =
    'flex items-stretch w-full rounded-full transition-all duration-200 overflow-hidden';
  const widthClasses = fullWidth ? 'w-full' : 'w-auto';

  const variantClasses = {
    outlined: [
      'border border-neutral-200',
      focused ? 'border-primary-500' : 'hover:border-neutral-500',
      error ? '!border-red-500 hover:!border-red-600' : '',
      disabled ? 'border-neutral-100' : '',
    ].join(' '),
    soft: [
      'bg-neutral-100/50',
      focused ? 'bg-neutral-100' : 'hover:bg-neutral-100',
      error ? 'bg-red-50 hover:bg-red-100' : '',
      disabled ? 'bg-neutral-50' : '',
    ].join(' '),
    plain: [
      focused ? 'bg-neutral-100/50' : 'hover:bg-neutral-50',
      error ? 'bg-red-50 hover:bg-red-100' : '',
      disabled ? 'bg-transparent' : '',
    ].join(' '),
  };

  const sizeClasses = {
    sm: 'min-h-8 text-xs',
    md: 'min-h-10 text-sm',
    lg: 'min-h-12 text-sm',
  };

  return twMerge(
    baseClasses,
    widthClasses,
    variantClasses[variant],
    sizeClasses[size]
  );
};

const getPaddingClasses = (
  size: InputSize,
  hasStartDecorator: boolean,
  hasEndDecorator: boolean
) => {
  const basePadding = {
    sm: 'py-2',
    md: 'py-2',
    lg: 'py-3',
  };

  const startPadding = {
    sm: hasStartDecorator ? 'pl-2' : 'pl-3',
    md: hasStartDecorator ? 'pl-2' : 'pl-3',
    lg: hasStartDecorator ? 'pl-3' : 'pl-4',
  };

  const endPadding = {
    sm: hasEndDecorator ? 'pr-2' : 'pr-3',
    md: hasEndDecorator ? 'pr-2' : 'pr-3',
    lg: hasEndDecorator ? 'pr-3' : 'pr-4',
  };

  return twMerge(basePadding[size], startPadding[size], endPadding[size]);
};

const getInputClasses = (disabled: boolean | undefined) => {
  return twMerge(
    'flex-1 min-w-0 bg-transparent outline-none truncate',
    'placeholder:text-neutral-500',
    disabled ? 'cursor-not-allowed text-neutral-300' : 'text-neutral-900'
  );
};

const getDecoratorClasses = (
  size: InputSize,
  disabled: boolean | undefined,
  position: 'start' | 'end'
) => {
  const baseClasses = 'flex items-center';

  const sizeClasses = {
    sm: 'text-base',
    md: 'text-base',
    lg: 'text-lg',
  };

  const paddingClasses = {
    start: {
      sm: 'pl-4',
      md: 'pl-4',
      lg: 'pl-4',
    },
    end: {
      sm: 'pr-4',
      md: 'pr-4',
      lg: 'pr-4',
    },
  };

  const colorClasses = disabled ? 'text-neutral-300' : 'text-neutral-500';

  return twMerge(
    baseClasses,
    sizeClasses[size],
    paddingClasses[position][size],
    colorClasses
  );
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      variant = 'outlined',
      size = 'md',
      startDecorator,
      endDecorator,
      error = false,
      fullWidth = false,
      disabled,
      className = '',
      sx = {},
      slotProps = {},
      ...props
    },
    ref
  ) => {
    const [focused, setFocused] = React.useState(false);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      props.onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(false);
      props.onBlur?.(e);
    };

    return (
      <div
        className={twMerge(
          getContainerClasses(
            variant,
            size,
            error,
            fullWidth,
            disabled,
            focused
          ),
          className,
          slotProps.root?.className
        )}
        style={sx}
      >
        {startDecorator && (
          <span
            className={twMerge(
              getDecoratorClasses(size, disabled, 'start'),
              slotProps.startDecorator?.className
            )}
          >
            {startDecorator}
          </span>
        )}
        <input
          ref={ref}
          disabled={disabled}
          className={twMerge(
            getInputClasses(disabled),
            getPaddingClasses(size, !!startDecorator, !!endDecorator),
            slotProps.input?.className
          )}
          onFocus={handleFocus}
          onBlur={handleBlur}
          {...props}
        />
        {endDecorator && (
          <span
            className={twMerge(
              getDecoratorClasses(size, disabled, 'end'),
              slotProps.endDecorator?.className
            )}
          >
            {endDecorator}
          </span>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
